import React, { useMemo, useState } from 'react'
import TableCell from '@material-ui/core/TableCell'

import PropTypes from 'prop-types'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableBody from '@material-ui/core/TableBody'
import styled from 'styled-components'
import moment from 'moment'
import Typography from '@material-ui/core/Typography'
import { groupBy } from 'lodash'
import { TextField, Select, MenuItem, FormControl, InputLabel, Button } from '@material-ui/core'
import ProjectSelection from '../../components/ProjectSelection'
import InvoiceActionButtons from './InvoiceActionButtons'
import {
  ApprovalsPeopleContainer,
  ApprovalViewContainer,
  BudgetContainer,
  StyledWeekContainer,
} from '../Approvals/ApprovalView.styles'
import { ProjectsTableContainer, StyledTableHead } from '../Approvals/ProjectsView.styles'
import { EditableCell } from '../Approvals/Components/ApprovalsTableColumn'
import { WeekTabButton } from '../Calendar/Header/styles'
import DiscountSection from './DiscountsView'

import ExpensesTable from './ExpensesSection'
import { getDiscountedTotal } from './utils'
import InvoiceExpertButtons from './InvoiceActionButtonsExperts'
import { exportInvoiceData } from '../../entities/project/service'
import { ksTheme } from '../../theme'

const ApprovalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  align-items: flex-end;
`

document.body.style.overflow = 'auto'
const TABS = {
  STAFF_FEES: 'Fees',
  STAFF_EXPENSES: 'Expenses',
  EXPERTS_TAB: 'Expert Fees & Expenses',
  DISCOUNTS: 'Discounts',
}

const ExpertFeesTable = ({
  peopleEntries,
  rates,
  people,
  onUpdateManyEntries,
  onReject,
  onSplitEntry,
  client,
  isStaffTab,
  expenses = [],
  invoiceProjects = [],
  onUpdateExpense,
  onSplitExpense,
}) => {
  const [filters, setFilters] = useState({
    dateRange: {
      start: '',
      end: '',
    },
    personId: '',
    isDeferred: '', // Boolean filter for defer logic
    status: '',
  })

  // Helper functions for styling
  const getStatusBackground = status => {
    if (status === 'WRITEOFF') return '#ffebee'
    if (status === 'RAW') return '#e8f5e8'
    if (status === 'BILL') return '#e3f2fd'
    return '#fff3e0'
  }

  const getStatusColor = status => {
    if (status === 'WRITEOFF') return '#c62828'
    if (status === 'RAW') return '#2e7d32'
    if (status === 'BILL') return '#1565c0'
    return '#ef6c00'
  }

  // Helper function to check if entry is deferred (following existing logic)
  const isDeferedToAnotherMonth = entry => {
    if (!entry.deferToMonth) return false
    const momentDefer = moment.utc(entry.deferToMonth)
    return momentDefer.isAfter(moment.utc(), 'month')
  }

  // Get unique people for filter dropdown
  const uniquePeople = useMemo(() => {
    const peopleInEntries = [...new Set(peopleEntries.map(entry => entry.personId))]
    return peopleInEntries.map(personId => ({
      id: personId,
      name: people[personId]?.fullName || 'Unknown',
    }))
  }, [peopleEntries, people])

  // Apply filters to entries
  const filteredEntries = useMemo(() => {
    return peopleEntries.filter(entry => {
      // Date range filter
      if (filters.dateRange.start) {
        const entryDate = moment.utc(entry.entryDate)
        const startDate = moment.utc(filters.dateRange.start)
        if (entryDate.isBefore(startDate, 'day')) return false
      }

      if (filters.dateRange.end) {
        const entryDate = moment.utc(entry.entryDate)
        const endDate = moment.utc(filters.dateRange.end)
        if (entryDate.isAfter(endDate, 'day')) return false
      }

      // Person filter
      if (filters.personId && entry.personId !== filters.personId) {
        return false
      }

      // Deferred filter (boolean logic)
      if (filters.isDeferred !== '') {
        const entryIsDeferred = isDeferedToAnotherMonth(entry)
        const filterDeferred = filters.isDeferred === 'true'
        if (entryIsDeferred !== filterDeferred) return false
      }

      // Status filter
      if (filters.status && entry.status !== filters.status) {
        return false
      }

      return true
    })
  }, [peopleEntries, filters])

  // Group filtered entries by week start date
  const entriesByWeek = groupBy(filteredEntries, entry =>
    moment
      .utc(entry.entryDate)
      .startOf('isoWeek')
      .format('YYYY-MM-DD'),
  )

  const handleFilterChange = (field, value) => {
    if (field === 'dateStart' || field === 'dateEnd') {
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [field === 'dateStart' ? 'start' : 'end']: value,
        },
      }))
    } else {
      setFilters(prev => ({
        ...prev,
        [field]: value,
      }))
    }
  }

  const clearFilters = () => {
    setFilters({
      dateRange: {
        start: '',
        end: '',
      },
      personId: '',
      isDeferred: '',
      status: '',
    })
  }

  const approvedExpenses = expenses.filter(entry => {
    const hasStatus = entry.approveState
    if (!hasStatus) return false
    return (
      entry.approveState.status === 'approved' &&
      invoiceProjects.some(project => project === entry.projectId) &&
      people[entry.personId].position === 'Contractor - Expert'
    )
  })

  return (
    <>
      <FiltersContainer>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
          <TextField
            label="Start Date"
            type="date"
            value={filters.dateRange.start}
            onChange={e => handleFilterChange('dateStart', e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 150 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={filters.dateRange.end}
            onChange={e => handleFilterChange('dateEnd', e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 150 }}
          />
        </div>
        <FormControl style={{ minWidth: 180 }}>
          <InputLabel>Person</InputLabel>
          <Select
            value={filters.personId}
            onChange={e => handleFilterChange('personId', e.target.value)}
          >
            <MenuItem value="">All People</MenuItem>
            {uniquePeople.map(person => (
              <MenuItem key={person.id} value={person.id}>
                {person.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 150 }}>
          <InputLabel>Deferred</InputLabel>
          <Select
            value={filters.isDeferred}
            onChange={e => handleFilterChange('isDeferred', e.target.value)}
          >
            <MenuItem value="">All Entries</MenuItem>
            <MenuItem value="true">Deferred Only</MenuItem>
            <MenuItem value="false">Not Deferred</MenuItem>
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            onChange={e => handleFilterChange('status', e.target.value)}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="WRITEOFF">Write Off</MenuItem>
            <MenuItem value="RAW">Raw</MenuItem>
            <MenuItem value="BILL">Bill</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={clearFilters}>
          Clear Filters
        </Button>
      </FiltersContainer>

      <TableContainer component={ProjectsTableContainer} rows={6}>
        <Table aria-label="simple table">
          <StyledTableHead>
            <TableRow>
              <TableCell align="left">Person</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">Comment</TableCell>
              <TableCell align="center">Hours</TableCell>
              <TableCell align="center">Rack Rate ($)</TableCell>
              <TableCell align="center">Total ($)</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </StyledTableHead>

          {Object.entries(entriesByWeek)
            .sort(([weekStartA], [weekStartB]) => weekStartA.localeCompare(weekStartB))
            .map(([weekStartStr, weekEntries]) => {
              const weekStart = moment.utc(weekStartStr)
              const weekEnd = moment.utc(weekStart).endOf('isoWeek')

              return (
                <TableBody key={`${weekStartStr}-${weekEnd.format('YYYY-MM-DD')}`}>
                  <TableRow>
                    <TableCell colSpan={8} style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
                      Week of {weekStart.format('MMM D')} – {weekEnd.format('MMM D, YYYY')} (
                      {weekEntries.length} {weekEntries.length === 1 ? 'entry' : 'entries'})
                    </TableCell>
                  </TableRow>

                  {weekEntries.map(entry => {
                    const personRate = rates[entry.projectId]?.[entry.personId]
                    const total = (personRate * entry.durationInHours).toFixed(2)

                    return (
                      <TableRow key={entry._id}>
                        <TableCell align="left">{people[entry.personId]?.fullName}</TableCell>
                        <TableCell align="center">
                          {moment.utc(entry.entryDate).format('MM/DD/YYYY')}
                        </TableCell>
                        <EditableCell
                          value={entry.comment}
                          onChange={val => {
                            return onUpdateManyEntries([
                              { ...entry, comment: val, originalComment: val },
                            ])
                          }}
                        />
                        <TableCell align="center">{entry.durationInHours}</TableCell>
                        <TableCell align="center">${personRate}</TableCell>
                        <TableCell align="center">${total}</TableCell>
                        <TableCell align="center">
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: getStatusBackground(entry.status),
                              color: getStatusColor(entry.status),
                            }}
                          >
                            {entry.status || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          {isStaffTab && (
                            <InvoiceActionButtons
                              entry={entry}
                              onUpdateManyEntries={onUpdateManyEntries}
                              onReject={onReject}
                              onSplitEntry={onSplitEntry}
                              rates={rates}
                              client={client}
                            />
                          )}
                          {!isStaffTab && (
                            <InvoiceExpertButtons
                              entry={entry}
                              onUpdateManyEntries={onUpdateManyEntries}
                              onReject={onReject}
                              onSplitEntry={onSplitEntry}
                              rates={rates}
                              client={client}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              )
            })}
        </Table>
      </TableContainer>
      {!isStaffTab && (
        <>
          <h2>Expenses</h2>
          <ExpensesTable
            people={people}
            onSplitExpense={onSplitExpense}
            expenses={approvedExpenses}
            client={client}
            onUpdateExpense={onUpdateExpense}
          />
        </>
      )}
    </>
  )
}

ExpertFeesTable.propTypes = {
  peopleEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  rates: PropTypes.object.isRequired,
  people: PropTypes.object.isRequired,
  onUpdateManyEntries: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onSplitEntry: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  isStaffTab: PropTypes.bool,
  expenses: PropTypes.arrayOf(PropTypes.object),
  invoiceProjects: PropTypes.arrayOf(PropTypes.string),
  onUpdateExpense: PropTypes.func,
  onSplitExpense: PropTypes.func,
}

const calculateInformativeTotals = ({ entries, rates, invoice = {}, expenses }) => {
  const entriesTotal = +entries.reduce((acc, entry) => {
    const personRate = rates[entry.projectId]?.[entry.personId]
    if (!personRate) {
      return acc
    }
    const calculatedTotal = personRate * entry.durationInHours
    return acc + calculatedTotal
  }, 0)

  const expensesTotal = +expenses.reduce((acc, expense) => {
    return acc + expense.amount
  }, 0)

  const total = entriesTotal + expensesTotal

  const entriesWriteOffs = +entries
    .filter(entry => entry.status === 'WRITEOFF')
    .reduce((acc, entry) => {
      const personRate = rates[entry.projectId]?.[entry.personId] || 0
      return acc + personRate * entry.durationInHours
    }, 0)

  const entriesDeferred = +entries
    .filter(entry => {
      if (!entry.deferToMonth) return false
      const momentDefer = moment.utc(entry.deferToMonth)
      return momentDefer.isAfter(moment.utc(), 'month')
    })
    .reduce((acc, entry) => {
      const personRate = rates[entry.projectId]?.[entry.personId] || 0
      return acc + personRate * entry.durationInHours
    }, 0)

  const expensesWriteOffs = +expenses
    .filter(expense => expense.status === 'WRITEOFF')
    .reduce((acc, expense) => acc + expense.amount, 0)

  const expensesDeferred = +expenses
    .filter(expense => {
      if (!expense.deferToMonth) return false
      const momentDefer = moment.utc(expense.deferToMonth)
      return momentDefer.isAfter(moment.utc(), 'month')
    })
    .reduce((acc, expense) => acc + expense.amount, 0)

  const totalWriteOffs = entriesWriteOffs + expensesWriteOffs
  const totalDeferred = entriesDeferred + expensesDeferred
  const totalAfterWriteOffs = total - (totalWriteOffs + totalDeferred)

  const { discount } = getDiscountedTotal({
    total: totalAfterWriteOffs,
    projectDiscount: invoice.projectDiscount,
  })

  const { discount: invoiceTotalDisc } = getDiscountedTotal({
    total: totalAfterWriteOffs,
    projectDiscount: invoice.invoiceDiscount,
  })

  const subtotal = total - (totalWriteOffs + totalDeferred)
  const totalDiscounts = invoiceTotalDisc + discount
  const totalInvoiceAmount = subtotal - totalDiscounts
  return {
    totalBeforeWriteOffs: total,
    total: subtotal,
    totalDiscounts,
    totalWriteOffs,
    totalDeferred,
    totalInvoiceAmount,
  }
}

const InvoiceManagement = ({
  projects,
  client,
  onUpdateManyEntries,
  timeEntries,
  people,
  onReject,
  rates,
  onSplitEntry,
  expenses = [],
  invoices: _invoices = [],
  updateInvoices,
  onUpdateExpense,
  onSplitExpense,
}) => {
  const invoices = useMemo(() => {
    return _invoices.filter(inv => {
      return inv.status === 'active' || inv.status === 'submitted' || !inv.status
    })
  }, [_invoices])
  const [invoiceIndex, setInvoiceIndex] = React.useState(0)
  const selectedInvoice = invoices[invoiceIndex]
  const [currentTab, setCurrentTab] = React.useState(TABS.STAFF_FEES)

  const invoiceProjects = useMemo(() => {
    if (!invoices) return []
    return invoices.flatMap(invoice => invoice.projects)
  }, [projects, invoices])

  const approvedExpenses = expenses.filter(entry => {
    /*
     * Business logic for filtering expenses:
     * 1. The expense must be approved.
     * 2. The expense must have a positive amount.
     * 3. The person must not be an expert (Contractor - Expert).
     * 4. The expense must belong to the current invoice's projects.
     * 5. The expense must either not be assigned to any invoice or belong to the current invoice.
     *
     * */

    const isApproved = entry.approveState && entry.approveState.status === 'approved'
    const amountPositive = entry.amount > 0
    const isExpert = people[entry.personId]?.position !== 'Contractor - Expert'
    const belongsToProject = invoiceProjects.some(project => project === entry.projectId)
    const belongsToCurrentInvoice =
      !entry.quickbooksInvoiceId ||
      selectedInvoice?.sections.some(
        section => section.quickbooksId === entry.quickbooksInvoiceId,
      ) ||
      false

    return isApproved && amountPositive && isExpert && belongsToProject && belongsToCurrentInvoice
  })

  const approvedEntries = timeEntries.filter(entry => {
    /*
     * Business logic for filtering time entries:
     * 1. The entry must be approved.
     * 2. The entry must be billable (ProjectRate).
     * 3. The entry must belong to the current invoice's projects.
     * 4. The entry must either not be assigned to any invoice or belong to the current invoice.
     * */
    if (!selectedInvoice?.sections) return false
    const isApproved = entry.approveState && entry.approveState.status === 'approved'
    if (!isApproved) return false

    const belongsToCurrentInvoice = selectedInvoice.sections.some(section => {
      return section.quickbooksId === entry.quickbooksInvoiceId
    })

    const isBillable = entry.billable === 'ProjectRate'
    const belongsToProject = invoiceProjects.some(project => project === entry.projectId)

    return isApproved && belongsToProject && belongsToCurrentInvoice && isBillable
  })

  const invoiceValues = calculateInformativeTotals({
    entries: approvedEntries,
    rates,
    invoice: selectedInvoice,
    expenses: approvedExpenses,
  })

  const {
    totalDiscounts,
    totalWriteOffs,
    totalDeferred,
    totalInvoiceAmount,
    totalBeforeWriteOffs,
  } = invoiceValues

  const handleExportData = async () => {
    try {
      // Prepare export data similar to what's shown in the table
      const exportEntries = approvedEntries.map(entry => {
        const personRate = rates[entry.projectId]?.[entry.personId] || 0
        const total = (personRate * entry.durationInHours).toFixed(2)

        return {
          person: people[entry.personId]?.fullName || 'Unknown',
          date: moment.utc(entry.entryDate).format('MM/DD/YYYY'),
          comment: entry.comment || '',
          hours: entry.durationInHours,
          role: people[entry.personId]?.position || 'Unknown',
          rate: personRate,
          total: parseFloat(total),
          status: entry.status || 'N/A',
        }
      })

      // Add expense entries if they exist
      const exportExpenses = approvedExpenses.map(expense => ({
        person: people[expense.personId]?.fullName || 'Unknown',
        date: moment.utc(expense.incurredDate).format('MM/DD/YYYY'),
        comment: expense.memo || '',
        hours: 0, // Expenses don't have hours
        role: people[expense.personId]?.position || 'Unknown',
        rate: 0, // Expenses don't have hourly rates
        total: expense.amount,
        status: expense.status || 'N/A',
      }))

      const exportData = {
        invoiceId: selectedInvoice?._id,
        invoiceName: selectedInvoice?.name,
        entries: [...exportEntries, ...exportExpenses],
        totals: {
          totalBeforeWriteOffs,
          totalWriteOffs,
          totalDiscounts,
          totalInvoiceAmount,
        },
      }

      const response = await exportInvoiceData({
        client,
        invoiceData: exportData,
      })

      // Handle file download
      const result = JSON.parse(response)
      if (result.success && result.fileData) {
        // Create a blob from the base64 data
        const binaryString = atob(result.fileData)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        const blob = new Blob([bytes], { type: result.mimeType })

        // Create download link
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.fileName
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    }
  }

  return (
    <ApprovalsContainer data-testid="invoiceManagement.root">
      <ApprovalViewContainer>
        <div>
          {invoices.length > 0 && (
            <ProjectSelection
              title="Select Invoice"
              projects={invoices}
              selectedProject={selectedInvoice?._id}
              setSelectedProject={invoice => {
                const newInvoice = invoices.findIndex(p => p._id === invoice)
                setInvoiceIndex(newInvoice)
              }}
              displayName={invoice => {
                return `${invoice.name} - ${moment(invoice.ship_date).format('MMMM YYYY')}`
              }}
            />
          )}
        </div>
        <ApprovalsPeopleContainer>
          <div
            data-testid="invoiceManagement.summary"
            style={{
              background: ksTheme.colors.paper,
              borderRadius: 16,
              padding: 24,
              marginBottom: 24,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              width: '100%',
              maxWidth: 1100,
              margin: '0 auto 24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 16,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {/* Subtotal Card */}
              <div
                data-testid="invoiceManagement.subtotal"
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 16,
                  minWidth: 140,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${ksTheme.colors.nightshade.medium}`,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: ksTheme.colors.gray[700],
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Subtotal
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: ksTheme.colors.black, fontWeight: 600, marginTop: 4 }}
                >
                  ${totalBeforeWriteOffs.toFixed(2)}
                </Typography>
              </div>

              {/* Deductions Group */}
              <div
                style={{
                  background: ksTheme.colors.red,
                  borderRadius: 12,
                  padding: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  gap: 24,
                }}
              >
                <div style={{ textAlign: 'center', minWidth: 90 }}>
                  <Typography
                    variant="caption"
                    style={{
                      color: ksTheme.colors.gray[900],
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Write Offs
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ color: ksTheme.colors.black, fontWeight: 600, marginTop: 4 }}
                  >
                    -${totalWriteOffs.toFixed(2)}
                  </Typography>
                </div>
                <div
                  style={{
                    borderLeft: `1px solid ${ksTheme.colors.gray[300]}`,
                    paddingLeft: 24,
                    textAlign: 'center',
                    minWidth: 90,
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: ksTheme.colors.gray[900],
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Deferred
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ color: ksTheme.colors.black, fontWeight: 600, marginTop: 4 }}
                  >
                    -${totalDeferred.toFixed(2)}
                  </Typography>
                </div>
                <div
                  style={{
                    borderLeft: `1px solid ${ksTheme.colors.gray[300]}`,
                    paddingLeft: 24,
                    textAlign: 'center',
                    minWidth: 90,
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: ksTheme.colors.gray[900],
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Discounts
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ color: ksTheme.colors.black, fontWeight: 600, marginTop: 4 }}
                  >
                    -${totalDiscounts.toFixed(2)}
                  </Typography>
                </div>
              </div>

              {/* Arrow */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: ksTheme.colors.gray[500],
                  fontSize: 24,
                }}
              >
                →
              </div>

              {/* Recognized Card */}
              <div
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 16,
                  minWidth: 160,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${ksTheme.colors.kellyGreen.dark}`,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: ksTheme.colors.gray[700],
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Recognized
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: ksTheme.colors.kellyGreen.dark, fontWeight: 600, marginTop: 4 }}
                >
                  ${(totalBeforeWriteOffs - totalWriteOffs).toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  style={{
                    color: ksTheme.colors.gray[500],
                    fontSize: 10,
                    marginTop: 4,
                    display: 'block',
                  }}
                >
                  Subtotal - Write Offs
                </Typography>
              </div>

              {/* Final Amount Card */}
              <div
                data-testid="invoiceManagement.invoiceTotal"
                style={{
                  background: `linear-gradient(135deg, ${ksTheme.colors.nightshade.light} 0%, ${ksTheme.colors.nightshade.dark} 100%)`,
                  borderRadius: 12,
                  padding: 16,
                  minWidth: 160,
                  textAlign: 'center',
                  boxShadow: '0 4px 15px rgba(88,86,255,0.3)',
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: ksTheme.colors.lilac.light,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Invoice Total
                </Typography>
                <Typography variant="h5" style={{ color: '#fff', fontWeight: 700, marginTop: 4 }}>
                  ${totalInvoiceAmount.toFixed(2)}
                </Typography>
              </div>

              {/* Export Button */}
              <Button
                variant="contained"
                data-testid="invoiceManagement.exportBtn"
                onClick={handleExportData}
                disabled={
                  !selectedInvoice ||
                  (approvedEntries.length === 0 && approvedExpenses.length === 0)
                }
                style={{
                  background: ksTheme.colors.nightshade.medium,
                  color: '#fff',
                  borderRadius: 12,
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(66,65,191,0.3)',
                  alignSelf: 'center',
                }}
              >
                Export Excel
              </Button>
            </div>
          </div>
          <StyledWeekContainer data-testid="invoiceManagement.tabs">
            {Object.values(TABS).map(tab => {
              const tabSlug = tab
                .replace(/\s+/g, '-')
                .replace(/&/g, 'and')
                .toLowerCase()
              return (
                <WeekTabButton
                  key={tab}
                  data-testid={`invoiceManagement.tab.${tabSlug}`}
                  minWidth="200px"
                  onClick={() => {
                    setCurrentTab(tab)
                  }}
                  selected={tab === currentTab}
                >
                  {tab}
                </WeekTabButton>
              )
            })}
          </StyledWeekContainer>
          {currentTab === TABS.STAFF_FEES && (
            <ExpertFeesTable
              onSplitExpense={onSplitExpense}
              peopleEntries={approvedEntries.filter(
                entry => people[entry.personId]?.position !== 'Contractor - Expert',
              )}
              rates={rates}
              people={people}
              onUpdateManyEntries={onUpdateManyEntries}
              onReject={onReject}
              onSplitEntry={onSplitEntry}
              client={client}
              isStaffTab
              expenses={expenses}
              onUpdateExpense={onUpdateExpense}
            />
          )}
          {currentTab === TABS.EXPERTS_TAB && (
            <ExpertFeesTable
              peopleEntries={approvedEntries.filter(
                entry => people[entry.personId].position === 'Contractor - Expert',
              )}
              rates={rates}
              people={people}
              onUpdateManyEntries={onUpdateManyEntries}
              onReject={onReject}
              onSplitEntry={onSplitEntry}
              client={client}
              expenses={expenses}
              invoiceProjects={invoiceProjects}
              onUpdateExpense={onUpdateExpense}
              onSplitExpense={onSplitExpense}
            />
          )}
          {currentTab === TABS.DISCOUNTS && (
            <DiscountSection
              invoice={selectedInvoice}
              invoiceValues={invoiceValues}
              client={client}
              updateInvoices={updateInvoices}
            />
          )}
          {currentTab === TABS.STAFF_EXPENSES && (
            <ExpensesTable
              people={people}
              expenses={approvedExpenses}
              isStaff
              client={client}
              onUpdateExpense={onUpdateExpense}
              onSplitExpense={onSplitExpense}
            />
          )}
        </ApprovalsPeopleContainer>
      </ApprovalViewContainer>
    </ApprovalsContainer>
  )
}

InvoiceManagement.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  client: PropTypes.object.isRequired,
  onUpdateManyEntries: PropTypes.func.isRequired,
  timeEntries: PropTypes.arrayOf(PropTypes.object),
  people: PropTypes.object,
  onReject: PropTypes.func,
  rates: PropTypes.object,
  onSplitEntry: PropTypes.func,
  expenses: PropTypes.arrayOf(PropTypes.object),
  invoices: PropTypes.arrayOf(PropTypes.object),
  updateInvoices: PropTypes.func,
  onUpdateExpense: PropTypes.func,
  onSplitExpense: PropTypes.func,
}

export { calculateInformativeTotals }

export default InvoiceManagement
